import { Editor, IAllProps } from '@tinymce/tinymce-react'
import classNames from 'classnames'
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'
import { Field, useField } from 'react-final-form'
import { useUser } from '../../../../contexts/userContext'
import { AllowedOption, FormFieldProps } from '../../../../types/commonTypes'
import { ERROR_OUTLINE_CLASS_NAME, FormFieldWithError } from '../FormFieldError/FormFieldWithError'
import fieldStyles from '../FormFields.module.css'
import './EditorFormField.module.css'
import styles from './EditorFormField.module.css'
import {
  audioTemplateCallback,
  filePickerCallback,
  getTinymceLanguage,
  imagesUploadHandler,
  videoTemplateCallback,
} from './helpers'
import { t } from 'i18next'

type InitProps = IAllProps['init']

const DEFAULT_PLUGINS_AI = 'lists link image video audio directionality ai emoticons'
const DEFAULT_PLUGINS_NO_AI = 'lists link image video audio directionality emoticons'
const BASIC_PLUGINS = 'link image video audio emoticons'
const DEFAULT_TOOLBAR_OPTIONS_AI =
  'bold italic subscript superscript undo redo removeFormat numlist bullist link image video audio aidialog aishortcuts emoticons'
const DEFAULT_TOOLBAR_OPTIONS_NO_AI =
  'bold italic subscript superscript undo redo removeFormat numlist bullist link image video audio emoticons'
const BASIC_TOOLBAR_OPTIONS = 'bold italic undo redo link image video audio emoticons'

export interface EditorFormFieldProps {
  onEditorChange?: (value: string) => void
  initProps?: InitProps
  additionalToolbarOptions?: string
  onlyBasicOptions?: boolean
  allowInputElements?: boolean
  onKeyUp?: (e: any) => void
  onCut?: (e: any) => void
  onUndo?: (e: any) => void
  onRedo?: (e: any) => void
  disabled?: boolean
  value?: string | null
}

export const EditorFormField = forwardRef<Editor, FormFieldProps<EditorFormFieldProps>>(
  (
    {
      name,
      label,
      srOnlyLabel,
      inline,
      onEditorChange,
      initProps,
      additionalToolbarOptions,
      onlyBasicOptions,
      fieldContainerClassName,
      validateOnlyIfDirty,
      allowInputElements,
      onKeyUp,
      onCut,
      onUndo,
      onRedo,
      disabled,
      value,
    },
    ref,
  ) => {
    const { user } = useUser()

    const DEFAULT_PLUGINS = user?.allowedOptions.includes(AllowedOption.AI_TOOLBAR)
      ? DEFAULT_PLUGINS_AI
      : DEFAULT_PLUGINS_NO_AI
    const DEFAULT_TOOLBAR_OPTIONS = user?.allowedOptions.includes(AllowedOption.AI_TOOLBAR)
      ? DEFAULT_TOOLBAR_OPTIONS_AI
      : DEFAULT_TOOLBAR_OPTIONS_NO_AI

    const language = useMemo(() => {
      return getTinymceLanguage(user?.language)
    }, [user?.language])

    const plugins = useMemo<string>(() => {
      return user?.allowedOptions.includes(AllowedOption.EXERCISE_CODE)
        ? (onlyBasicOptions ? BASIC_PLUGINS : DEFAULT_PLUGINS) + ' code'
        : onlyBasicOptions
        ? BASIC_PLUGINS
        : DEFAULT_PLUGINS
    }, [DEFAULT_PLUGINS, onlyBasicOptions, user?.allowedOptions])

    const toolbar = useMemo<string>(() => {
      return [
        onlyBasicOptions ? BASIC_TOOLBAR_OPTIONS : DEFAULT_TOOLBAR_OPTIONS,
        additionalToolbarOptions,
        user?.allowedOptions.includes(AllowedOption.EXERCISE_CODE) ? 'code' : null,
      ]
        .filter((item) => item != null)
        .join(' ')
    }, [onlyBasicOptions, DEFAULT_TOOLBAR_OPTIONS, additionalToolbarOptions, user?.allowedOptions])

    const { input, meta } = useField(name)
    const [initialValue, setInitialValue] = useState(input.value)

    useEffect(() => {
      if (value != null) setInitialValue(value)
    }, [value])

    const handleEditorChange = useCallback(
      (value: string) => {
        input.onChange(value)
        onEditorChange?.(value)
      },
      [input, onEditorChange],
    )

    const handleBlur = useCallback(() => {
      input.onBlur()
    }, [input])

    const handleFocusIn = () => {
      input.onFocus()
    }

    return (
      <FormFieldWithError validateOnlyIfDirty={validateOnlyIfDirty} name={name} errorClassName={styles.editorError}>
        <Field name={name} component='input' type='hidden' />
        <div className={classNames(inline && fieldStyles.multilineInlineField, fieldContainerClassName)}>
          {label && (
            <label className={classNames(srOnlyLabel && 'sr-only', fieldStyles.label)} id={name}>
              {label}
            </label>
          )}
          <div
            className={classNames(
              styles.editorWrapper,
              meta.active && styles.editorWrapper_active,
              ERROR_OUTLINE_CLASS_NAME,
            )}
          >
            <Editor
              ref={ref}
              //apiKey={process.env.REACT_APP_TINY_MCE_API_KEY}
              licenseKey='gpl'
              onEditorChange={handleEditorChange}
              initialValue={initialValue}
              onBlur={handleBlur}
              onFocusIn={handleFocusIn}
              onKeyUp={onKeyUp}
              onCut={onCut}
              onUndo={onUndo}
              onRedo={onRedo}
              aria-labelledby={name}
              disabled={disabled}
              tinymceScriptSrc={'/tinymce7_24/tinymce.min.js'}
              init={{
                plugins: plugins,
                paste_as_text: true,
                toolbar: toolbar,
                ai_request: ai_request,
                menubar: false,
                contextmenu: false,
                language,
                language_url: language ? `/tinymce7_24/langs/${language}.js` : undefined,
                directionality: language === 'ar' ? 'rtl' : 'ltr',
                images_upload_handler: imagesUploadHandler,
                media_alt_source: false,
                media_poster: false,
                file_picker_types: 'media',
                file_picker_callback: filePickerCallback,
                audio_template_callback: audioTemplateCallback,
                video_template_callback: videoTemplateCallback,
                invalid_elements: `h1,h2,h3,h4,h5,h6,font${allowInputElements ? '' : ',input'}`,
                invalid_styles: {
                  '*': 'line-height font-family font-size font-weight',
                },
                branding: false,
                elementpath: false,
                height: 200,
                body_class: 'tiny-mce-body-custom',
                content_css: '/tinymce7_24/custom_content.css',
                ...initProps,
              }}
            />
          </div>
        </div>
      </FormFieldWithError>
    )
  },
)

const API_KEY = 'DXNhfbmBtIZMHNeGh3Ootdi3xNSmqih3napOlggWBwObBIkSGSytJQQJ99BEACfhMk5XJ3w3AAABACOGLlBn'

const ai_request = (request: any, respondWith: any) => {
  const KEEP_LANGUAGE_PROMPT =
    "Keep the content you are modifying in it's original language, unless you are sepcifically asked to translate it. "
  const openAiOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${API_KEY}`,
    },
    body: JSON.stringify({
      model: 'gpt-4o',
      temperature: 0.7,
      max_tokens: 800,
      messages: [{ role: 'user', content: KEEP_LANGUAGE_PROMPT + request.prompt }],
    }),
  }
  respondWith.string((signal: any) =>
    window
      .fetch(
        'https://saige.openai.azure.com/openai/deployments/gpt-4o/chat/completions?api-version=2025-01-01-preview',
        { signal, ...openAiOptions },
      )
      .then(async (response) => {
        if (response) {
          const data = await response.json()
          if (data.error) {
            throw new Error(`${data.error.type}: ${data.error.message}`)
          } else if (response.ok) {
            // Extract the response content from the data returned by the API
            if (data?.choices[0]?.message?.content?.trim().includes('<img')) {
              return '```html' + t('ai_plugin.image_error_message', "Unfortunately I can't generate images.") + '```'
            } else {
              return data?.choices[0]?.message?.content?.trim()
            }
          }
        } else {
          throw new Error('Failed to communicate with the Azure AI API')
        }
      }),
  )
}
